import React, { PureComponent, Fragment } from 'react'
import PropTypes from 'prop-types'
import FontAwesomeIcon from '@fortawesome/react-fontawesome'
import { faAngleDoubleRight, faAngleDoubleDown, faHourglassHalf, faClipboardCheck, faTh } from '@fortawesome/fontawesome-free-solid'
import moment from 'moment'

import cattleXsImage from '../../../assets/img/cattle-xs.png'
import sheepXsImage from '../../../assets/img/sheep-xs.png'
import goatXsImage from '../../../assets/img/goat-xs.png'

import BackButton from '../../BackButton/BackButton'
import StarRating from '../../StarRating/StarRating'
import Api from '../../../services/api'
import mapSurvey from './mapSurvey'

// Icons match the categories dropdown in Upcoming Auctions (FilterKind)
const FILTERS = [
  { key: 'all', name: 'All', icon: <FontAwesomeIcon icon={faTh} /> },
  { key: 'cattle', name: 'Cattle', icon: cattleXsImage },
  { key: 'sheep', name: 'Sheep', icon: sheepXsImage },
  { key: 'goat', name: 'Goat', icon: goatXsImage },
  { key: 'awaiting', name: 'Awaiting', icon: <FontAwesomeIcon icon={faHourglassHalf} /> },
  { key: 'completed', name: 'Completed', icon: <FontAwesomeIcon icon={faClipboardCheck} /> }
]

class Surveys extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      surveys: [],
      loaded: false,
      activeFilter: 'all',
      expanded: {}
    }
    this.setFilter = this.setFilter.bind(this)
    this.toggleExpand = this.toggleExpand.bind(this)
    this.rateQuestion = this.rateQuestion.bind(this)
    this.submitSurvey = this.submitSurvey.bind(this)
    this.goToAssessment = this.goToAssessment.bind(this)
  }

  componentDidMount() {
    if (this.props.history && this.props.history.action !== 'POP') {
      window.scroll(0, 0)
    }
    this.getSurveys()
  }

  async getSurveys() {
    const res = await Api.getSurveys()
    const surveys = (res && res.surveys) ? res.surveys.map(mapSurvey) : []
    this.setState({ surveys, loaded: true })
  }

  setFilter(key) {
    this.setState({ activeFilter: key })
  }

  toggleExpand(id) {
    this.setState(prev => ({
      expanded: { ...prev.expanded, [id]: !prev.expanded[id] }
    }))
  }

  rateQuestion(surveyId, questionIndex, rating) {
    this.setState(prev => ({
      surveys: prev.surveys.map(survey => {
        if (survey.id !== surveyId) return survey
        return {
          ...survey,
          questions: survey.questions.map((q, i) =>
            i === questionIndex ? { ...q, rating } : q
          )
        }
      })
    }))
  }

  goToAssessment(lot) {
    // Clicking the Lot details opens the assessment form in the Results section
    // (works from both the Awaiting Completion and Completed tabs).
    this.props.history.push({
      pathname: `/main/results/${lot.kindTitle}/${lot.searchNumber}/${lot.searchNumericId}`,
      state: { from: 'surveys' }
    })
  }

  averageRating(questions) {
    if (!questions.length) return 0
    const total = questions.reduce((sum, q) => sum + q.rating, 0)
    return Math.round(total / questions.length)
  }

  isComplete(survey) {
    return survey.questions.every(q => q.rating > 0)
  }

  async submitSurvey(surveyId) {
    const survey = this.state.surveys.find(item => item.id === surveyId)
    if (!survey) return

    const answers = survey.questions.map(question => ({
      question: question.id,
      rating: question.rating
    }))

    const res = await Api.submitSurvey(surveyId, answers)
    if (!res || !res.survey) return

    this.setState(prev => ({
      surveys: prev.surveys.map(item =>
        item.id === surveyId ? { ...item, status: 'completed' } : item
      ),
      expanded: { ...prev.expanded, [surveyId]: false }
    }))

    // Let the app-level notification bar refresh its outstanding count.
    window.dispatchEvent(new Event('surveys-updated'))
  }

  matchesFilter(survey) {
    const { activeFilter } = this.state
    switch (activeFilter) {
      case 'all':
        return true
      case 'cattle':
      case 'sheep':
      case 'goat':
        return survey.stockType === activeFilter
      case 'awaiting':
      case 'completed':
        return survey.status === activeFilter
      default:
        return true
    }
  }

  renderLotCard(lot) {
    return (
      <div className='survey-lot-card' onClick={() => this.goToAssessment(lot)} title='View assessment form'>
        <div className='survey-lot-image' style={{ backgroundImage: `url(${lot.image})` }} />
        <div className='survey-lot-body'>
          <div className='survey-lot-topbar'>
            <span className='survey-lot-name'>{lot.lotName} -</span>
            <span className='survey-lot-status'>{lot.status}</span>
            <span className='survey-lot-location'>{lot.location}</span>
          </div>
          <p className='survey-lot-title'>{lot.title}</p>
          <p className='survey-lot-price'>{lot.status} {lot.price}</p>
          <div className='survey-lot-tags-row'>
            <div className='survey-lot-tags'>
              {lot.tags.map((tag, i) => (
                <span className='survey-lot-tag' key={i}>{tag}</span>
              ))}
            </div>
            <span className='survey-lot-agent'>{lot.agent}</span>
          </div>
        </div>
      </div>
    )
  }

  renderAwaitingRow(survey) {
    const expanded = !!this.state.expanded[survey.id]
    return (
      <div className='survey-row awaiting' key={survey.id}>
        <div className='survey-row-header'>
          <div className='survey-row-info'>
            <p className='survey-row-date'>{moment(survey.date).format('DD/MM/YY')} - {survey.type}</p>
            <p className='survey-row-title'>{survey.lot.title}</p>
          </div>
          <div className='survey-row-actions'>
            <button className='survey-rate-btn' onClick={() => this.toggleExpand(survey.id)}>Rate</button>
            <button className='survey-expand-btn' onClick={() => this.toggleExpand(survey.id)}>
              <FontAwesomeIcon icon={expanded ? faAngleDoubleDown : faAngleDoubleRight} />
            </button>
          </div>
        </div>
        {expanded && (
          <div className='survey-row-details'>
            {this.renderLotCard(survey.lot)}
            <div className='survey-questions'>
              {survey.questions.map((question, index) => (
                <div className='survey-question' key={index}>
                  <p className='survey-question-text'>{question.text}</p>
                  <StarRating
                    value={question.rating}
                    editable
                    onChange={(rating) => this.rateQuestion(survey.id, index, rating)}
                  />
                </div>
              ))}
            </div>
            <div className='survey-submit-row'>
              <button
                className='survey-submit-btn'
                disabled={!this.isComplete(survey)}
                onClick={() => this.submitSurvey(survey.id)}
              >
                Submit Survey
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  renderCompletedRow(survey) {
    const expanded = !!this.state.expanded[survey.id]
    return (
      <div className='survey-row completed' key={survey.id}>
        <div className='survey-row-header'>
          <div className='survey-row-info'>
            <p className='survey-row-date'>{moment(survey.date).format('DD/MM/YY')} - {survey.type}</p>
            <p className='survey-row-title'>{survey.lot.title}</p>
          </div>
          <div className='survey-row-actions'>
            <StarRating value={this.averageRating(survey.questions)} />
            <button className='survey-view-btn' onClick={() => this.toggleExpand(survey.id)}>
              <span>View Details</span>
              <span className='icon-wrapper'>
                <FontAwesomeIcon icon={expanded ? faAngleDoubleDown : faAngleDoubleRight} />
              </span>
            </button>
          </div>
        </div>
        {expanded && (
          <div className='survey-row-details'>
            {this.renderLotCard(survey.lot)}
            <div className='survey-questions'>
              {survey.questions.map((question, index) => (
                <div className='survey-question' key={index}>
                  <p className='survey-question-text'>{question.text}</p>
                  <StarRating value={question.rating} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  render() {
    if (!this.state.loaded) {
      return <div />
    }
    const surveys = this.state.surveys.filter(survey => this.matchesFilter(survey))
    const awaiting = surveys.filter(survey => survey.status === 'awaiting')
    const completed = surveys.filter(survey => survey.status === 'completed')

    return (
      <div className='surveys-wrapper'>
        <div className='fg-header'>Surveys</div>
        <p className='fg-paragraph'>
          Rate your experience of Lots you have sold or purchased. <br></br>
          The survey will take less than 1 minute to complete. There are 5 questions you rate from 1 - 5 stars. Your individual ratings will remain confidential and will not be shared with the purchaser or seller. Your feedback will contribute to the overall star rating displayed on future assessment forms.


        </p>
        <div className='top-btns'>
          <BackButton onPress={() => this.props.history.goBack()} name='Back to My Admin' />
        </div>
        <hr className='separator' />

        <div className='survey-filters'>
          <div className='title'>Filters</div>
          <div className='filter-container'>
            {FILTERS.map(filter => (
              <div
                key={filter.key}
                className={`survey-filter-btn ${filter.key} ${this.state.activeFilter === filter.key ? 'active' : ''}`}
                onClick={() => this.setFilter(filter.key)}
              >
                <span>{filter.name}</span>
                <span className='icon-wrapper'>
                  {React.isValidElement(filter.icon)
                    ? filter.icon
                    : <img src={filter.icon} alt={filter.key} className='icon-blackened' />}
                </span>
              </div>
            ))}
          </div>
        </div>

        {this.state.activeFilter !== 'completed' && (
          <Fragment>
            <div className='survey-section-header awaiting'>
              <FontAwesomeIcon icon={faHourglassHalf} />
              <span>Awaiting Completion</span>
            </div>
            {awaiting.length
              ? awaiting.map(survey => this.renderAwaitingRow(survey))
              : <p className='survey-empty'>No surveys awaiting completion.</p>}
          </Fragment>
        )}

        {this.state.activeFilter !== 'awaiting' && (
          <Fragment>
            <div className='survey-section-header completed'>
              <FontAwesomeIcon icon={faClipboardCheck} />
              <span>Completed Surveys</span>
            </div>
            {completed.length
              ? completed.map(survey => this.renderCompletedRow(survey))
              : <p className='survey-empty'>No completed surveys yet.</p>}
          </Fragment>
        )}
      </div>
    )
  }
}

Surveys.propTypes = {
  history: PropTypes.object.isRequired
}

export default Surveys
